import React from 'react';
import moment from 'moment';
import { BaseFormViewModel } from '../common/ViewModel';
import {
    onFieldChange,
    onReactSelectChanged,
    toasty,
} from '../common/forms/FormElements';
import CommonContext from '../Common';
import { MomentDateFormat } from '../MomentDateFormat';
import { ApiRoutes } from '../ApiRoutes';
import { AppNavPaths } from '../AppNavPaths';
import SlideForm from '../common/forms/SlideForm';
import { util } from '../Util';
import { handleFormSaveError } from '../common/forms/ValidationError';
import {
    getDurationInDays,
    getEventDisplayDate,
    LongTermSchedule,
    toEmployeeString,
} from './LongTermSchedule';
import { faCalendarWeek } from '@fortawesome/free-solid-svg-icons';
import _ from 'lodash-es';
import { JobOverride } from '../job/JobOverride';
import LongTermSchedleBasicDetails from './LongTermScheduleBasicDetails';
import UnpublishedLongTermScheduleDetails from './UnpublishedLongTermScheduleDetails';
import PublishedLongTermScheduleDetails from './PublishedLongTermScheduleDetails';
import ReleasedLongTermScheduleDetails from './ReleasedLongTermScheduleDetails';
import LongTermScheduleActionManager from './LongTermScheduleActionManager';
import LtsCloneWarning from './LtsCloneWarning';
import {
    isRequestedRescheduleDateValid,
    LongTermScheduleDayCancelApiRequest,
} from './LongTermScheduleDayCancelApiRequest';
import JobNoteButton from './JobNoteButton';
import './LongTermScheduleForm.scss';
import JobStartTimeButton from './JobStartTimeButton';

export default class LongTermScheduleForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.longTermScheduleForm = React.createRef();

        const stateBase = {
            crews: [],
            contracts: [],
            companies: [],
            schedulers: [],
            superintendents: [],
            contractPhases: [],
            assignmentConflicts: [],
            longTermSchedule: new LongTermSchedule(),
            originalLongTermSchedule: new LongTermSchedule(),
            jobOverride: new JobOverride(),
            ...new BaseFormViewModel(),
            activeDaysValid: false,
            eventDate: null,
            alsoDispatchNotifications: false,
            cancelRequest: new LongTermScheduleDayCancelApiRequest(),
            cancelWorkflowEnabled: false,
            maxCrewEndDate: null,
            activeDayCount: 0,
            job: null,
            originalJob: null,
            showNonFolinoContracts: false,
            neitherCrewNorEmployeeSet: true,
            phasesValid: true,
            equipmentTypeCountsValid: true,
            existingJobContractPhases: [],
            reschedulingEffect: {},
            equipmentTypeCounts: [],
            equipmentTypeIdsRequiringMoves: [],
            arcGisLocations: [],
            loadingSupplementalData: false,
            anyNotificationsSent: false,
            changesSignaled: false,
            jobResourceOverrides: [],
        };

        this.state = stateBase;
        this.onSubmit = this.onSubmit.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onSelectChange = this.onSelectChange.bind(this);
        this.resetForm = this.resetForm.bind(this);
        this.isNew = this.isNew.bind(this);
        this.onSelectChanged = this.onSelectChanged.bind(this);
        this.onEquipmentGroupChange = this.onEquipmentGroupChange.bind(this);
        this.onJobResourceChange = this.onJobResourceChange.bind(this);
        this.onJobContractPhaseChange =
            this.onJobContractPhaseChange.bind(this);
        this.onStartDateChange = this.onStartDateChange.bind(this);
        this.onEndDateChange = this.onEndDateChange.bind(this);
        this.onArcGisLocationChange = this.onArcGisLocationChange.bind(this);
        this.onJobOverrideArcGisLocationChange =
            this.onJobOverrideArcGisLocationChange.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    onChange = onFieldChange;
    onSelectChanged = onReactSelectChanged;

    onEquipmentGroupChange = (e) => {
        const value = e.target.value;

        const index = Number(e.target.name.match(/\d+/));

        const { longTermSchedule } = this.state;

        longTermSchedule.scheduleEquipmentGroupCounts[index].count = value;

        this.setState({ longTermSchedule });
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    onClose = () => {
        this.resetForm();
        this.props.toggleShow(false);
        this.context.setFormOpened(false);
        this.setState({ longTermSchedule: new LongTermSchedule() });
        this.props.onClose();
    };

    onDelete = async () => {
        const { longTermSchedule } = this.state;

        const response = await util.fetch.delete(
            ApiRoutes.longTermSchedule.delete(longTermSchedule.id)
        );

        if (response && response.data) {
            toasty.success(
                'Long-Term Schedule Deleted',
                'Long-Term Schedule deleted successfully.'
            );

            this.onClose();
        }
    };

    onSelectChange = onReactSelectChanged;

    validateActiveDays = (longTermSchedule) =>
        Boolean(
            longTermSchedule &&
            longTermSchedule.activeDays &&
            longTermSchedule.activeDays.length > 0
        );

    onJobResourceChange = (changeData, e) => {
        const { applicationGroupId, equipmentGroupId } = changeData;

        let value = 0;

        const targetValue = e.target.value;

        if (isNaN(targetValue)) {
            return;
        }

        value = Number(targetValue);

        if (value < 0) {
            return;
        }

        const { job } = this.state;
        const dayName = this.getPublishedDayName();

        const jobResource = job.resources.find(
            (jr) =>
                (applicationGroupId &&
                    jr.applicationGroupId == applicationGroupId) ||
                (equipmentGroupId && jr.equipmentGroupId == equipmentGroupId)
        );

        jobResource[dayName] = value;

        this.setState({ job, changesSignaled: true });
    };

    saveLongTermSchedule = async (longTermSchedule) => {
        const { activeDayCount, eventDate } = this.state;
        const duration = getDurationInDays(longTermSchedule);

        if (
            !this.validateActiveDays(longTermSchedule) ||
            duration <= 0 ||
            activeDayCount <= 0
        ) {
            return false;
        }

        this.setState({ errors: {}, saving: true });

        const url = this.isNew()
            ? ApiRoutes.longTermSchedule.create()
            : ApiRoutes.longTermSchedule.update();

        const response = await util.fetch
            .andGetResponse(util.fetch.types.post, url, longTermSchedule)
            .catch(this.handleSaveError);

        if (response) {
            toasty.success(
                'Long-Term Schedule Saved',
                'Long-Term Schedule saved successfully.'
            );

            const ltsId = this.isNew() ? response : longTermSchedule.id;

            await this.openLongTermSchedule(ltsId, eventDate);
        }

        this.setState({ saving: false });
    };

    saveJobAndJobOverride = async () => {
        const { longTermSchedule, jobOverride, job, eventDate } = this.state;

        this.setState({ saving: true });

        if (job.resources) {
            job.resources = job.resources.map((r) => {
                if (!_.isEmpty(r.jobResourceOverrides)) {
                    r.jobResourceOverrides = r.jobResourceOverrides.map(
                        (o) => ({
                            ...o,
                            id:
                                !o.id || o.id.constructor === String
                                    ? null
                                    : o.id,
                        })
                    );
                }

                return r;
            });
        }

        const [overrideResponse, jobResponse] = await Promise.all([
            util.fetch
                .andGetResponse(
                    util.fetch.types.post,
                    ApiRoutes.jobOverride.upsert(longTermSchedule.id),
                    jobOverride
                )
                .catch(this.handleSaveError),
            util.fetch
                .andGetResponse(
                    util.fetch.types.put,
                    ApiRoutes.job.updateForLts(job.id),
                    { job, eventDate }
                )
                .catch(this.handleSaveError),
        ]);

        if (overrideResponse && jobResponse) {
            if (overrideResponse.data) {
                toasty.success(
                    'Long-Term Schedule Amended',
                    'Long-Term Schedule amended successfully.'
                );

                await this.openLongTermSchedule(longTermSchedule.id, eventDate);
            }

            if (!overrideResponse.error && !jobResponse.error) {
                this.setState({ changesSignaled: false });
            }
        }

        this.setState({ saving: false });
    };

    async onSubmit() {
        const { longTermSchedule } = this.state;

        if (!longTermSchedule.isPublished) {
            await this.saveLongTermSchedule(longTermSchedule);
        } else {
            await this.saveJobAndJobOverride();
        }
    }

    isNew = () => {
        const { longTermSchedule } = this.state;

        return Boolean(
            !longTermSchedule ||
            !longTermSchedule.id ||
            longTermSchedule.id <= 0
        );
    };

    getNewJobOverride = (eventDate) => {
        const jobOverride = new JobOverride();
        jobOverride.overrideDate = eventDate;

        return jobOverride;
    };

    openLongTermSchedule = async (longTermScheduleId, eventDate) => {
        const initJobOverride = this.getNewJobOverride(eventDate);

        await this.setState({
            loading: true,
            longTermSchedule: new LongTermSchedule(),
            originalLongTermSchedule: new LongTermSchedule(),
            jobOverride: initJobOverride,
            eventDate,
            neitherCrewNorEmployeeSet: true,
            anyNotificationsSent: false,
        });

        await this.populateState();

        let jobOverride = null,
            job = null,
            existingJobContractPhases = [],
            equipmentTypeCounts = [],
            anyNotificationsSent = false,
            jobResourceOverrides = [];

        if (longTermScheduleId > 0) {
            const longTermSchedule = await util.fetch.js(
                ApiRoutes.longTermSchedule.getSingle(longTermScheduleId)
            );

            if (longTermSchedule && longTermSchedule.isPublished) {
                const apiEventDate = moment(eventDate).format(
                    MomentDateFormat.ApiUrlRequest
                );

                [
                    jobOverride,
                    job,
                    equipmentTypeCounts,
                    anyNotificationsSent,
                    jobResourceOverrides,
                ] = await Promise.all([
                    util.fetch.js(
                        ApiRoutes.jobOverride.get(
                            longTermScheduleId,
                            apiEventDate
                        )
                    ),
                    util.fetch.js(
                        ApiRoutes.job.getPublishedJob(
                            longTermScheduleId,
                            apiEventDate
                        )
                    ),
                    util.fetch.js(
                        ApiRoutes.equipmentTypeCount.forDate(apiEventDate)
                    ),
                    util.fetch.post(
                        ApiRoutes.jobAssignment.anyNotificationsSent(),
                        {
                            longTermScheduleId,
                            eventDate,
                        }
                    ),
                    util.fetch.js(
                        ApiRoutes.jobResourceOverride.get(
                            longTermScheduleId,
                            apiEventDate
                        )
                    ),
                ]);

                if (job && job.resources) {
                    job.resources = job.resources.map((jobResource) => ({
                        ...jobResource,
                        jobResourceOverrides: jobResourceOverrides.filter(
                            (jobResourceOverride) =>
                                jobResourceOverride.jobResourceId ===
                                jobResource.id
                        ),
                    }));
                }

                jobOverride.overrideDate = eventDate;

                existingJobContractPhases = job.jobContractPhases.filter(
                    (jcp) => jcp.dayOfWeek == moment(eventDate).day()
                );
            }

            const activeDaysValid = this.validateActiveDays(longTermSchedule);

            this.setState(
                {
                    longTermSchedule,
                    activeDaysValid,
                    originalLongTermSchedule: _.cloneDeep(longTermSchedule),
                    jobOverride,
                    job,
                    originalJob: _.cloneDeep(job),
                    existingJobContractPhases,
                    reschedulingEffect: {},
                    equipmentTypeCounts,
                    anyNotificationsSent,
                    changesSignaled: false,
                    jobResourceOverrides,
                },
                () => {
                    this.recalculateActiveDays();
                    this.validateCrewOrEmployeeSet(true); // don't touch ad hoc crew name
                    this.getContractPhasesAndLocations();
                }
            );
        } else {
            const { equipmentGroups } = this.props;
            if (equipmentGroups) {
                let longTermSchedule = new LongTermSchedule();

                longTermSchedule.scheduleEquipmentGroupCounts =
                    equipmentGroups.map((eg) => {
                        return {
                            equipmentGroupId: eg.id,
                            count: 0,
                        };
                    });

                this.setState({
                    longTermSchedule,
                    job: null,
                    originalJob: null,
                });
            }
        }

        this.setState({
            loading: false,
            phasesValid: true,
            equipmentTypeCountsValid: true,
            changesSignaled: false,
        });
    };

    onResourcesUpdated = async () => {
        this.setState({ loading: true });
        const { longTermSchedule, eventDate } = this.state;

        const apiEventDate = moment(eventDate).format(
            MomentDateFormat.ApiUrlRequest
        );

        const [job, equipmentTypeCounts] = await Promise.all([
            util.fetch.js(
                ApiRoutes.job.getPublishedJob(longTermSchedule.id, apiEventDate)
            ),
            util.fetch.js(ApiRoutes.equipmentTypeCount.forDate(apiEventDate)),
        ]);

        this.setState({
            job,
            originalJob: _.cloneDeep(job),
            loading: false,
            equipmentTypeCounts,
        });
    };

    async populateState() {
        const [schedulers, equipmentTypeIdsRequiringMoves, superintendents] =
            await Promise.all([
                util.fetch.js(ApiRoutes.employees.schedulers()),
                util.fetch.js(ApiRoutes.equipmentType.requiringMoves()),
                util.fetch.js(ApiRoutes.employees.superintendents()),
            ]);

        this.setState({
            schedulers,
            equipmentTypeIdsRequiringMoves,
            superintendents,
        });
    }

    resetForm = () => {
        this.setState({
            formValidated: false,
            longTermSchedule: new LongTermSchedule(),
            eventDate: null,
            alsoDispatchNotifications: false,
            saving: false,
            jobOverride: new JobOverride(),
            cancelRequest: new LongTermScheduleDayCancelApiRequest(),
            cancelWorkflowEnabled: false,
            changesSignaled: false,
        });
    };

    fetchMaxCrewEndDate = async (crewId) => {
        if (!this.isNew() || !crewId) {
            this.setState({ maxCrewEndDate: null });

            return;
        }

        const maxCrewEndDateResponse = await util.fetch.get(
            ApiRoutes.longTermSchedule.getMaxCrewEndDate(crewId)
        );

        const { maxCrewEndDate } = maxCrewEndDateResponse;

        this.setState({ maxCrewEndDate });
    };

    validateCrewOrEmployeeSet = (doNotUpdateAdHocCrewName) => {
        const { longTermSchedule } = this.state;

        const neitherCrewNorEmployeeSet = Boolean(
            !longTermSchedule.crewId && !longTermSchedule.leadEmployeeId
        );

        if (!doNotUpdateAdHocCrewName && this.isCrewAdHoc()) {
            longTermSchedule.adHocCrewName = this.getLeadEmployeeName();
        }

        this.setState({ neitherCrewNorEmployeeSet });
    };

    onCrewChange = (selectedCrew) => {
        let { longTermSchedule } = this.state;

        longTermSchedule.crewId = selectedCrew?.id;
        longTermSchedule.leadEmployeeId = selectedCrew?.crewLeadEmployeeId;

        this.setState({ longTermSchedule }, () => {
            this.fetchMaxCrewEndDate(selectedCrew?.id);
            this.validateCrewOrEmployeeSet();
            this.recalculateActiveDays();
        });
    };

    onLeadEmployeeChange = (selectedEmployee) => {
        const { longTermSchedule } = this.state;

        const leadEmployeeId = (selectedEmployee || {}).id;

        longTermSchedule.leadEmployeeId = leadEmployeeId;

        this.setState({ longTermSchedule }, () =>
            this.validateCrewOrEmployeeSet()
        );
    };

    getContractPhasesAndLocations = async () => {
        this.setState({ loadingSupplementalData: true });
        const { longTermSchedule } = this.state;

        if (!longTermSchedule || !longTermSchedule.contractId) {
            this.setState({ contractPhases: [], arcGisLocations: [] });
            return;
        }

        try {
            const [contractPhases, arcGisLocations] = await Promise.all([
                util.fetch.js(
                    ApiRoutes.contractPhases.forContract(
                        longTermSchedule.contractId,
                        longTermSchedule.id
                    )
                ),
                util.fetch.js(
                    ApiRoutes.arcGisLocations.activeForContract(
                        longTermSchedule.contractId
                    )
                ),
            ]);

            if (
                !longTermSchedule.isPublished &&
                arcGisLocations &&
                arcGisLocations.filter((agl) => agl.isActive).length == 1
            ) {
                longTermSchedule.arcGisLocationId = arcGisLocations[0].id;
            }

            this.setState({
                contractPhases,
                arcGisLocations,
                longTermSchedule,
                loadingSupplementalData: false,
            });
        } catch (e) {
            console.error({ e });

            this.setState({ loadingSupplementalData: false });
        }
    };

    onContractChange = (selectedContract) => {
        let { longTermSchedule } = this.state;

        longTermSchedule.contractId = (selectedContract || {}).id;
        longTermSchedule.longTermScheduleContractPhases = [];
        longTermSchedule.arcGisLocationId = null;

        this.setState({ longTermSchedule }, () =>
            this.getContractPhasesAndLocations()
        );
    };

    onLongTermScheduleDayCancelOptionChange = (selectedCancelOption) => {
        let { cancelRequest } = this.state;

        cancelRequest.longTermScheduleDayCancelOptionId = (
            selectedCancelOption || {}
        ).id;

        this.setState({ cancelRequest });
    };

    onRescheduleDateChange = (dateChangeEvent) => {
        let { cancelRequest } = this.state;

        cancelRequest.requestedRescheduleDate = dateChangeEvent?.target?.value;

        this.setState({ cancelRequest });
    };

    onCancellationTypeChange = (jobCancellationType) => {
        let { cancelRequest } = this.state;

        cancelRequest.cancellationTypeId = (jobCancellationType || {}).value;

        this.setState({ cancelRequest });
    };

    onLongTermScheduleContractPhaseChange = (entityContractPhaseState) => {
        const { longTermSchedule } = this.state;

        const { entityContractPhases, isValid } = entityContractPhaseState;

        longTermSchedule.longTermScheduleContractPhases = entityContractPhases;

        this.setState({ longTermSchedule, phasesValid: isValid });
    };

    onProjectManagerChange = (projectManager) => {
        const { longTermSchedule } = this.state;

        longTermSchedule.projectManagerId = projectManager
            ? projectManager.id
            : null;

        this.setState({ longTermSchedule });
    };

    onSuperintendentChange = (superintendent) => {
        const { longTermSchedule } = this.state;

        longTermSchedule.superintendentId = superintendent
            ? superintendent.id
            : null;

        this.setState({ longTermSchedule });
    };

    onJobContractPhaseChange = (selectedContractPhaseState) => {
        const { job, eventDate } = this.state;

        const dayOfWeek = moment(eventDate).day();
        const jobId = job.id;

        const { isValid, entityContractPhases } = selectedContractPhaseState;

        const newJobContractPhases = entityContractPhases.map((scp) => {
            return { ...scp, jobId, dayOfWeek };
        });

        job.jobContractPhases = job.jobContractPhases
            .filter((jcp) => jcp.dayOfWeek != dayOfWeek)
            .concat(newJobContractPhases);

        this.setState({ job, phasesValid: isValid });
    };

    onJobNotesUpdated = (newJobNotes) => {
        const { job, originalJob } = this.state;

        job.notes = newJobNotes;
        originalJob.notes = newJobNotes;

        this.setState({ job, originalJob });
    };

    onJobStartTimeUpdated = (newJobStartTime) => {
        const { job, originalJob } = this.state;

        job.startTime = newJobStartTime;
        originalJob.jobStartTime = newJobStartTime;

        this.setState({ job, originalJob });
    };

    publishLongTermSchedule = async () => {
        const { longTermSchedule } = this.state;

        const { history } = this.props;

        history.push(
            `${AppNavPaths.JobNewForLongTermSchedule}/${longTermSchedule.id}`
        );
    };

    assignLongTermSchedule = async () => {
        const { longTermSchedule } = this.state;

        try {
            const assignmentResponse = await util.fetch.andGetResponse(
                util.fetch.types.post,
                ApiRoutes.longTermSchedule.markAssigned(longTermSchedule.id)
            );

            if (assignmentResponse) {
                toasty.success('Long-Term Schedule Assigned');

                this.onClose();
            }
        } catch (e) {
            console.error({ e });
        }
    };

    activeDaysChange = (event) => {
        let { longTermSchedule, activeDaysValid } = this.state;
        const { target } = event;

        const { value, checked } = target;

        const numberValue = Number(value);

        if (checked) {
            longTermSchedule.activeDays.push(numberValue);

            activeDaysValid = this.validateActiveDays(longTermSchedule);
        } else {
            // https://stackoverflow.com/a/20690490
            longTermSchedule.activeDays = longTermSchedule.activeDays.filter(
                (sd) => sd != numberValue
            );

            activeDaysValid = this.validateActiveDays(longTermSchedule);
        }

        this.setState({ longTermSchedule, activeDaysValid }, () =>
            this.recalculateActiveDays()
        );
    };

    getCrewName = () => {
        const { longTermSchedule } = this.state;
        const { crews } = this.props;

        const crew = crews.find((c) => longTermSchedule.crewId == c.id);

        return crew.name;
    };

    sendNotifications = async () => {
        this.setState({ saving: true });
        const { longTermSchedule, eventDate, alsoDispatchNotifications } =
            this.state;

        if (!alsoDispatchNotifications) {
            return true;
        }

        const resendNoResponse = this.isAlreadyReleased();
        const crewName = this.getCrewName();

        const data = {
            longTermScheduleId: longTermSchedule.id,
            date: eventDate,
            resendNoResponse,
        };

        const notifyUrl = ApiRoutes.jobAssignment.notify();

        const response = await util.fetch
            .andGetResponse(util.fetch.types.post, notifyUrl, data)
            .catch(this.handleSaveError);

        this.setState({ saving: false });

        if (
            !response ||
            (response.errorMessages && response.errorMessages.length)
        ) {
            toasty.error(
                'Error Sending Notifications',
                `There was an error sending notifications to ${crewName}.`
            );

            await this.openLongTermSchedule(longTermSchedule.id, eventDate);

            console.error(response.errorMessages);

            return false;
        }

        if (resendNoResponse) {
            this.onClose();

            toasty.success(
                'Notifications Sent',
                `Notifications for the ${getEventDisplayDate(eventDate)} assignment were successfully sent to ${crewName}.`
            );
        }

        return true;
    };

    releaseDay = async () => {
        this.setState({ saving: true });

        const { longTermSchedule, eventDate, alsoDispatchNotifications } =
            this.state;
        const crewName = this.getCrewName();

        this.setState({ errors: {}, saving: true });

        const releaseUrl = ApiRoutes.longTermSchedule.release(
            longTermSchedule.id,
            eventDate
        );

        const response = await util.fetch
            .andGetResponse(util.fetch.types.post, releaseUrl, longTermSchedule)
            .catch(this.handleSaveError);

        this.setState({ saving: false });

        if (response && (await this.sendNotifications())) {
            this.onClose();

            const successMessage = alsoDispatchNotifications
                ? `${crewName} has been notified of their ${getEventDisplayDate(eventDate)} assignment.`
                : `The job for ${getEventDisplayDate(eventDate)} was successfully released to ${crewName}. Notifications were not sent.`;

            toasty.success('Job Assignment Released', successMessage);
        }
    };

    isAlreadyReleased = () => {
        const { longTermSchedule, eventDate } = this.state;

        if (
            !eventDate ||
            !longTermSchedule.isPublished ||
            !longTermSchedule.releasedDays
        ) {
            return false;
        }

        return longTermSchedule.releasedDays.some((d) =>
            moment(eventDate).isSame(d, 'day')
        );
    };

    toggleCancelMode = async () => {
        let {
            cancelWorkflowEnabled,
            longTermSchedule,
            eventDate,
            reschedulingEffect,
        } = this.state;

        if (_.isEmpty(reschedulingEffect)) {
            const apiEventDate = moment(eventDate).format(
                MomentDateFormat.ApiUrlRequest
            );

            reschedulingEffect = await util.fetch.js(
                ApiRoutes.longTermScheduleCancellation.check(
                    longTermSchedule.id,
                    apiEventDate
                )
            );

            this.setState({ reschedulingEffect });
        }

        this.setState({
            cancelWorkflowEnabled: !cancelWorkflowEnabled,
        });
    };

    doesCancelRequestRequireTime = () => {
        const isReleased = this.isAlreadyReleased();

        if (!isReleased) {
            return false;
        }

        const { cancelRequest } = this.state;

        // client cancel
        return cancelRequest.cancellationTypeId == 1;
    };

    doesCancelRequestRequireDateSelection = () => {
        const { cancelRequest } = this.state;
        const { longTermScheduleDayCancelOptions } = this.props;

        if (!cancelRequest || !longTermScheduleDayCancelOptions) {
            return false;
        }

        const longTermScheduleDayCancelOption =
            longTermScheduleDayCancelOptions.find(
                (ltsdco) =>
                    ltsdco.id == cancelRequest.longTermScheduleDayCancelOptionId
            );

        return longTermScheduleDayCancelOption?.rescheduleToSpecifiedDate;
    };

    cancelRequestIsValid = () => {
        const { cancelRequest, eventDate } = this.state;

        if (!cancelRequest) {
            return false;
        }

        const isReleased = this.isAlreadyReleased();

        if (isReleased) {
            return Boolean(
                cancelRequest.cancellationTypeId &&
                (!this.doesCancelRequestRequireTime() ||
                    cancelRequest.cancellationTime)
            );
        }

        if (
            this.doesCancelRequestRequireDateSelection() &&
            !isRequestedRescheduleDateValid(cancelRequest, eventDate)
        ) {
            return false;
        }

        return Boolean(cancelRequest.longTermScheduleDayCancelOptionId);
    };

    onCancelClick = async () => {
        this.setState({ saving: true });

        const { longTermSchedule, cancelRequest, eventDate, job } = this.state;

        const data = {
            ...cancelRequest,
            longTermScheduleId: longTermSchedule.id,
            date: eventDate,
            jobId: job.id,
        };

        const url = ApiRoutes.longTermScheduleCancellation.cancelDay();

        const response = await util.fetch
            .andGetResponse(util.fetch.types.post, url, data)
            .catch(this.handleSaveError);

        if (response) {
            this.onClose();

            toasty.success(
                'Long-Term Schedule - Day Cancelled',
                'Long-Term Schedule - Day cancelled successfully.'
            );
        } else {
            this.setState({ saving: false });
        }
    };

    isAlreadyCancelled = () => {
        const { longTermSchedule, eventDate } = this.state;
        const { dayCancellations } = this.props;

        return dayCancellations.some(
            (dc) =>
                dc.longTermScheduleId == longTermSchedule.id &&
                moment(eventDate).isSame(dc.date, 'day')
        );
    };

    recalculateActiveDays = async () => {
        const { longTermSchedule } = this.state;

        const [activeDayResponse, assignmentConflicts] = await Promise.all([
            util.fetch.andGetResponse(
                util.fetch.types.post,
                ApiRoutes.longTermSchedule.getActiveDayCount(),
                longTermSchedule
            ),
            util.fetch.andGetResponse(
                util.fetch.types.post,
                ApiRoutes.longTermSchedule.getConflicts(),
                longTermSchedule
            ),
        ]);

        let activeDayCount = 0;

        if (activeDayResponse) {
            activeDayCount = activeDayResponse.activeDayCount;
        }

        this.setState({ activeDayCount, assignmentConflicts });
    };

    getPublishedDayName = () => {
        const { eventDate } = this.state;

        if (!eventDate) {
            return null;
        }

        return moment(eventDate).format('dddd').toLocaleLowerCase();
    };

    getAvailableLeadEmployees = () => {
        const { longTermSchedule } = this.state;
        const { crews, employees } = this.props;

        const crew = crews.find((c) => c.id == longTermSchedule.crewId);

        if (!crew) {
            return employees;
        }

        return employees.filter((e) =>
            crew.crewEmployeeMemberships.includes(e.id)
        );
    };

    getLeadEmployeeName = () => {
        const employee = this.getLeadEmployee();

        return employee ? toEmployeeString(employee) : null;
    };

    getLeadEmployee = () => {
        const { longTermSchedule, job } = this.state;
        const { employees, crews } = this.props;

        if (job && job.foremenId) {
            const employee = employees.find((e) => e.id == job.foremenId);

            if (employee) {
                return employee;
            }
        }

        if (longTermSchedule.publishedCrew) {
            const employee = employees.find(
                (e) => e.id == longTermSchedule.publishedCrew.leadEmployeeId
            );

            if (employee) {
                return employee;
            }
        }

        const crew = crews.find((c) => c.id == longTermSchedule.crewId) || {};

        return (
            employees.find(
                (e) =>
                    e.id == crew.leadEmployeeId ||
                    e.id == longTermSchedule.leadEmployeeId
            ) || {}
        );
    };

    isCrewAdHoc = () => {
        const { longTermSchedule } = this.state;
        const { crews } = this.props;

        const crew = crews.find((c) => c.id == longTermSchedule.crewId);

        return Boolean(
            longTermSchedule.leadEmployeeId &&
            (!crew || (crew && crew.forInternalUse))
        );
    };

    onDeleteClicked = () => {
        if (
            window.confirm(
                'Are you sure you want to soft delete this long-term schedule?'
            )
        ) {
            this.onDelete();
        }
    };

    onStartDateChange = (e) => {
        const { longTermSchedule } = this.state;
        longTermSchedule.startDate = e.target.value;

        if (
            moment(longTermSchedule.startDate).isAfter(
                longTermSchedule.endDate,
                'day'
            )
        ) {
            longTermSchedule.endDate = e.target.value;
        }

        this.setState({ longTermSchedule }, () => {
            this.recalculateActiveDays();
        });
    };

    onEndDateChange = (e) => {
        const { longTermSchedule } = this.state;
        longTermSchedule.endDate = e.target.value;

        this.setState({ longTermSchedule }, () => {
            this.recalculateActiveDays();
        });
    };

    onArcGisLocationChange = (arcGisLocation) => {
        if (!arcGisLocation) {
            return;
        }

        const { longTermSchedule } = this.state;

        longTermSchedule.arcGisLocationId = arcGisLocation.id;

        this.setState({ longTermSchedule, changesSignaled: true });
    };

    onJobOverrideArcGisLocationChange = (arcGisLocation) => {
        const { jobOverride } = this.state;

        jobOverride.arcGisLocationId = (arcGisLocation || {}).id;

        this.setState({ jobOverride, changesSignaled: true });
    };

    onCloneClick = () => {
        const { longTermSchedule, eventDate } = this.state;

        const clonedLts = _.cloneDeep(longTermSchedule);

        clonedLts.id = 0;
        clonedLts.publishedCrew = null;
        clonedLts.isPublished = false;

        clonedLts.isClone = true;
        clonedLts.releasedDays = [];

        const doubleClone = _.cloneDeep(clonedLts);

        const jobOverride = this.getNewJobOverride(eventDate);

        this.setState({
            longTermSchedule: clonedLts,
            originalLongTermSchedule: doubleClone,
            assignmentConflicts: [],
            job: null,
            originalJob: null,
            jobOverride,
            changesSignaled: false,
        });
    };

    signalChange = () => {
        this.setState({ changesSignaled: true });
    };

    getFormTitle = () => {
        const { loading, longTermSchedule } = this.state;

        if (loading) {
            return 'Loading';
        }

        if (this.isNew()) {
            return 'Add Job';
        }

        if (longTermSchedule.isPublished) {
            return 'Edit Order';
        }

        return 'Edit Job';
    };

    render() {
        const {
            formValidated,
            validationMessage,
            longTermSchedule,
            loading,
            // alsoDispatchNotifications,
            saving,
            neitherCrewNorEmployeeSet,
            phasesValid,
            equipmentTypeCountsValid,
        } = this.state;

        const { show, errors, canCreateLts } = this.props;

        const isCancelled = this.isAlreadyCancelled();
        const isReleased = this.isAlreadyReleased();

        const crewIsAdHoc = this.isCrewAdHoc();

        const formTitle = this.getFormTitle();

        const availableLeadEmployees = this.getAvailableLeadEmployees();
        const leadEmployee = this.getLeadEmployee();

        const cancelRequestRequiresTime = this.doesCancelRequestRequireTime();
        const cancelRequestRequiresDateSelection =
            this.doesCancelRequestRequireDateSelection();
        const publishedDayName = this.getPublishedDayName();
        const cancelRequestValid = this.cancelRequestIsValid();

        const showClone = Boolean(longTermSchedule.id > 0);

        return (
            <SlideForm
                className="z-idx-50"
                loading={loading}
                show={show}
                id="longTermScheduleForm"
                formIcon={faCalendarWeek}
                formTitle={formTitle}
                ref={this.formRef}
                setIsValidated={(formValidated) =>
                    this.setState({ formValidated })
                }
                isValidated={
                    formValidated && phasesValid && equipmentTypeCountsValid
                }
                onSubmit={this.onSubmit}
                onClose={this.onClose}
                onSave={this.onSave}
                errors={errors}
                onClearErrors={this.onClearErrors}
                validationMessage={validationMessage}
                entityId={longTermSchedule.id}
                hideToolbar={isCancelled}
                readOnly={saving || loading}
                saving={saving}
                hideDelete={longTermSchedule.isPublished}
                saveDisabled={
                    neitherCrewNorEmployeeSet ||
                    !phasesValid ||
                    !equipmentTypeCountsValid
                }
                size="xl"
                onDelete={this.onDeleteClicked}
                showClone={showClone && canCreateLts}
                onCloneClick={this.onCloneClick}
            >
                <LtsCloneWarning {...this.state} />

                <LongTermSchedleBasicDetails
                    {...this.state}
                    {...this.props}
                    isReleased={isReleased}
                    isCancelled={isCancelled}
                    onChange={this.onChange}
                    onCrewChange={this.onCrewChange}
                    onContractChange={this.onContractChange}
                    onProjectManagerChange={this.onProjectManagerChange}
                    crewIsAdHoc={crewIsAdHoc}
                    availableLeadEmployees={availableLeadEmployees}
                    leadEmployee={leadEmployee}
                    onLeadEmployeeChange={this.onLeadEmployeeChange}
                    onSuperintendentChange={this.onSuperintendentChange}
                />

                <UnpublishedLongTermScheduleDetails
                    {...this.state}
                    {...this.props}
                    onChange={this.onChange}
                    onStartDateChange={this.onStartDateChange}
                    onEndDateChange={this.onEndDateChange}
                    activeDaysChange={this.activeDaysChange}
                    onLongTermScheduleContractPhaseChange={
                        this.onLongTermScheduleContractPhaseChange
                    }
                    signalChange={this.signalChange}
                />

                <PublishedLongTermScheduleDetails
                    {...this.state}
                    {...this.props}
                    onResourcesUpdated={this.onResourcesUpdated}
                    onJobContractPhaseChange={this.onJobContractPhaseChange}
                    isReleased={isReleased}
                    publishedDayName={publishedDayName}
                    onChange={this.onChange}
                    onJobResourceChange={this.onJobResourceChange}
                    onJobOverrideArcGisLocationChange={
                        this.onJobOverrideArcGisLocationChange
                    }
                    saving={saving}
                    signalChange={this.signalChange}
                />

                <ReleasedLongTermScheduleDetails
                    {...this.state}
                    {...this.props}
                    onChange={this.onChange}
                    onEquipmentGroupChange={this.onEquipmentGroupChange}
                    isReleased={isReleased}
                    onArcGisLocationChange={this.onArcGisLocationChange}
                    signalChange={this.signalChange}
                />

                <div className="d-flex fg-2">
                    <div className="w-50">
                        <JobNoteButton
                            {...this.state}
                            onJobNotesUpdated={this.onJobNotesUpdated}
                        />
                    </div>
                    <div className="w-50">
                        <JobStartTimeButton
                            {...this.state}
                            onJobStartTimeUpdated={this.onJobStartTimeUpdated}
                        />
                    </div>
                </div>

                <LongTermScheduleActionManager
                    {...this.state}
                    {...this.props}
                    isReleased={isReleased}
                    isCancelled={isCancelled}
                    toggleCancelMode={this.toggleCancelMode}
                    onCancellationTypeChange={this.onCancellationTypeChange}
                    onLongTermScheduleDayCancelOptionChange={
                        this.onLongTermScheduleDayCancelOptionChange
                    }
                    onRescheduleDateChange={this.onRescheduleDateChange}
                    onCancelClick={this.onCancelClick}
                    cancelRequestRequiresTime={cancelRequestRequiresTime}
                    cancelRequestRequiresDateSelection={
                        cancelRequestRequiresDateSelection
                    }
                    releaseDay={this.releaseDay}
                    cancelRequestValid={cancelRequestValid}
                    publishLongTermSchedule={this.publishLongTermSchedule}
                    assignLongTermSchedule={this.assignLongTermSchedule}
                />
            </SlideForm>
        );
    }
}
